import { useCallback, useRef, useState } from 'react'
import { buildBackendUrl } from '@/lib/backend-url'
import { actions } from '@/store'

const NLP_EXTRACT_ENDPOINT = buildBackendUrl('/nlp-extract')

// Simple in-memory cache for extraction results during the session
const extractionCache = new Map()

export function useTaskNLPExtraction() {
  const [isLoading, setIsLoading] = useState(false)
  const abortControllerRef = useRef(null)

  const extractTaskDetails = useCallback(async (text, coords = null) => {
    if (!text || text.trim().length < 5) return null

    const cacheKey = `${text.trim().toLowerCase()}-${coords ? `${coords.lat},${coords.lng}` : 'no-coords'}`
    if (extractionCache.has(cacheKey)) {
      return extractionCache.get(cacheKey)
    }

    abortControllerRef.current?.abort()
    const controller = new AbortController()
    abortControllerRef.current = controller

    setIsLoading(true)
    try {
      const response = await fetch(NLP_EXTRACT_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          q: text,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          lat: coords?.lat,
          lng: coords?.lng,
        }),
        signal: controller.signal,
        credentials: 'include',
      })

      if (!response.ok) {
        throw new Error(`NLP extraction failed: ${response.status}`)
      }

      const data = await response.json()
      extractionCache.set(cacheKey, data)
      actions.incrementAIAction()
      return data
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('NLP extraction error:', error)
      }
      return null
    } finally {
      setIsLoading(false)
    }
  }, [])

  return {
    extractTaskDetails,
    isLoading,
  }
}
